import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { isPartner } from '../../../helpers/appType';
import { reportError } from '../../../helpers/reportError';
import {
  ConversationAttachmentEntity,
  ConversationMessageAudience,
  ConversationMessageEntity,
  ConversationMessageWithStatus,
  SendConversationMessageDto,
} from '../../../types/conversation.types';
import { patchChatMessageByClientId, upsertChatMessage } from '../chatCache';
import { postConversationMessage } from '../conversationTransport';

export type ChatDraft = {
  body?: string;
  attachmentIds?: number[];
  replyToId?: number;
  orderId?: number;
  /** How the pin reads on the optimistic bubble. CLIENT ONLY. */
  orderLabel?: string;
  /** Members only; they are pushed even through a mute. */
  mentionUserIds?: number[];
  /**
   * Every current member but the sender counts as mentioned - groups only.
   * The `@all` itself has to be in `body`; the backend never writes it there.
   */
  mentionAll?: boolean;
  /**
   * Dispatcher and website threads: who this is for. Omitted is everyone -
   * the customer included. See `SendConversationMessageDto`.
   */
  audience?: ConversationMessageAudience;
  /**
   * Attachments to show on the optimistic bubble, built from the local file so
   * the picture is there the instant the message appears - the way Telegram
   * shows the photo you just sent rather than a gap where it will be.
   *
   * CLIENT ONLY. The payload below is assembled field by field precisely so
   * this can never reach the API, which knows attachments by id.
   */
  previewAttachments?: ConversationAttachmentEntity[];
  /**
   * Uploads that have to finish before the message can be posted, resolving to
   * the `attachmentIds` to send with it.
   *
   * Given as a function rather than awaited by the caller so the bubble goes up
   * FIRST and the file uploads underneath it - which is the whole difference
   * between a picture that appears in the conversation the moment you press
   * send and a modal that sits there spinning until the upload finishes.
   *
   * The message shows as `pending` for as long as this is running, and a
   * rejection marks it failed like any other send failure. Do not report the
   * error here - `dispatch` does, so a retry does not toast twice.
   *
   * CLIENT ONLY, as above.
   */
  prepare?: () => Promise<number[]>;
};

type SendOptions = {
  conversationId: number | string;
  /** Sender to show on the optimistic bubble before the server answers. */
  sender: ConversationMessageEntity['sender'];
  onSent?: () => void;
};

/**
 * Optimistic send.
 *
 * `clientMessageId` is generated here once per message and reused on every
 * retry. The backend treats it as an idempotency key, so a send that times out
 * and is retried returns the original message instead of creating a second one
 * - which is the whole reason a failed bubble can offer "Retry" safely.
 */
export const useSendChatMessage = ({
  conversationId,
  sender,
  onSent,
}: SendOptions) => {
  const queryClient = useQueryClient();
  const [sending, setSending] = useState(false);

  /**
   * What each in-flight message was actually sent with, keyed by its
   * `clientMessageId`, so Retry re-sends the same thing.
   *
   * The bubble on screen cannot stand in for this: it carries a local preview
   * of the picture, not the `attachmentIds` the API needs, so retrying from it
   * alone would quietly re-send a photo message as its caption. An entry is
   * dropped once the message is through; what is left is one small object per
   * message that failed and was never retried.
   */
  const draftsRef = useRef(new Map<string, ChatDraft>());

  const post = useCallback(
    async (clientMessageId: string, draft: ChatDraft) => {
      const payload: SendConversationMessageDto = {
        clientMessageId,
        ...(draft.body ? { body: draft.body } : {}),
        ...(draft.attachmentIds?.length
          ? { attachmentIds: draft.attachmentIds }
          : {}),
        ...(draft.replyToId ? { replyToId: draft.replyToId } : {}),
        ...(draft.orderId ? { orderId: draft.orderId } : {}),
        ...(draft.mentionUserIds?.length
          ? { mentionUserIds: draft.mentionUserIds }
          : {}),
        ...(draft.mentionAll ? { mentionAll: true } : {}),
        ...(draft.audience && draft.audience !== 'everyone'
          ? { audience: draft.audience }
          : {}),
      };

      try {
        setSending(true);
        const { data } = await postConversationMessage(conversationId, payload);
        // Reconciles with the optimistic row by clientMessageId - and is a
        // no-op if the socket echo already did it.
        upsertChatMessage(queryClient, conversationId, data.data);
        draftsRef.current.delete(clientMessageId);
        onSent?.();
      } catch (error) {
        patchChatMessageByClientId(
          queryClient,
          conversationId,
          clientMessageId,
          { pending: false, failed: true }
        );
        reportError(error);
      } finally {
        setSending(false);
      }
    },
    [conversationId, queryClient, onSent]
  );

  /**
   * Everything between "the bubble is on screen" and "the server has it":
   * finish the uploads the draft is waiting on, then post.
   *
   * The resolved ids are written back over the draft, so a POST that fails
   * after a successful upload retries as a plain post - re-running `prepare`
   * would upload the same file a second time and orphan the first copy.
   */
  const dispatch = useCallback(
    async (clientMessageId: string, draft: ChatDraft) => {
      let resolved = draft;

      if (draft.prepare) {
        try {
          const attachmentIds = await draft.prepare();
          resolved = { ...draft, attachmentIds, prepare: undefined };
          draftsRef.current.set(clientMessageId, resolved);
        } catch (error) {
          patchChatMessageByClientId(
            queryClient,
            conversationId,
            clientMessageId,
            { pending: false, failed: true }
          );
          reportError(error);
          return;
        }
      }

      await post(clientMessageId, resolved);
    },
    [conversationId, queryClient, post]
  );

  const send = useCallback(
    (draft: ChatDraft) => {
      const clientMessageId = uuidv4();

      const optimistic: ConversationMessageWithStatus = {
        // `id` is a placeholder; nothing looks this row up by id - the list is
        // keyed on clientMessageId precisely so this value never matters.
        id: 0,
        // Real on admin, the sentinel on the merchant side. Only ever read back
        // out by the socket handler, which resolves the cache key itself.
        conversationId: Number(conversationId) || 0,
        /*
         * The side of whoever is composing: the merchant is the outside `user`,
         * everyone in the admin app is `internal` (in a direct or a group both
         * ends are). Alignment does not come from this - `isOwnMessage` reads
         * the sender id - but the field has to be filled to render at all, and
         * getting it wrong would flip the bubble when the server echo lands.
         */
        side: isPartner ? 'user' : 'internal',
        sender,
        body: draft.body ?? null,
        attachments: draft.previewAttachments ?? [],
        orderId: draft.orderId ?? null,
        orderLabel: draft.orderLabel ?? null,
        replyTo: null,
        clientMessageId,
        systemEvent: null,
        systemMeta: null,
        mentionUserIds: draft.mentionUserIds ?? [],
        seen: false,
        editedAt: null,
        deletedAt: null,
        createdAt: new Date().toISOString(),
        // Only the triage bot attaches these; a merchant's own message never
        // carries one. Spelled out because the entity requires them.
        actionProposal: null,
        ticket: null,
        quickReplies: null,
        isAssistant: false,
        /* So a note or a fleet message is coloured as one from the moment it
           goes up, not only once the server echoes it back. */
        audience: draft.audience ?? 'everyone',
        pending: true,
      };

      draftsRef.current.set(clientMessageId, draft);
      upsertChatMessage(queryClient, conversationId, optimistic);
      return dispatch(clientMessageId, draft);
    },
    [conversationId, queryClient, sender, dispatch]
  );

  /**
   * Retry reuses the original `clientMessageId` and the draft it was sent with.
   *
   * The id is what makes this safe to press twice: the backend treats it as an
   * idempotency key, so a send that timed out after the server had already
   * stored it comes back as the same message rather than a second one. The
   * draft is what makes the retry mean the same thing as the send - the caller
   * only has the rendered message to go on, which is missing whatever the
   * upload produced.
   */
  const retry = useCallback(
    (message: ConversationMessageWithStatus, draft: ChatDraft) => {
      patchChatMessageByClientId(
        queryClient,
        conversationId,
        message.clientMessageId,
        { pending: true, failed: false }
      );
      const original = draftsRef.current.get(message.clientMessageId);
      return dispatch(message.clientMessageId, original ?? draft);
    },
    [conversationId, queryClient, dispatch]
  );

  return { send, retry, sending };
};
